import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';

const CancellationPolicy = () => {
  return (
    <LegalPageLayout
      title="Cancellation Policy"
      updated="October 13, 2026"
    >
      <Typography paragraph>
        This Cancellation Policy applies to shipment requests and
        related marketplace transactions on HaggleFreight, operated
        by GoCourier Inc. It supplements the Terms of Service,
        Refund Policy, and applicable accepted transportation
        agreement.
      </Typography>

      <Typography paragraph>
        Cancelling a Platform record and ending the legal
        obligations between a Customer and Transportation Provider
        are not necessarily the same action. Users must address
        their accepted commitments and applicable law.
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Cancellation Before Carrier Acceptance
      </Typography>

      <Typography paragraph>
        A Customer may withdraw an unaccepted shipment request
        without a shipment-cancellation fee. A Transportation
        Provider viewing the request, submitting a bid, or
        discussing pricing does not by itself create a confirmed
        transportation agreement.
      </Typography>

      <Typography paragraph>
        A separately purchased subscription or other service
        remains subject to its accepted purchase terms. Withdrawal
        of a free listing does not authorize a new Platform fee.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Cancellation After Carrier Acceptance
      </Typography>

      <Typography paragraph>
        After the parties mutually accept a transportation
        agreement, a party seeking cancellation must promptly
        notify the other party and use the available cancellation
        process.
      </Typography>

      <Typography paragraph>
        A cancellation charge applies only when supported by
        accepted terms or another lawful basis. Where advance
        disclosure and agreement are required, the amount or
        calculation method and the party entitled to the charge
        must have been identified before booking.
      </Typography>

      <Typography paragraph>
        This policy does not establish a default percentage,
        minimum penalty, or unlimited right to recover every
        claimed expense. Additional rights arising from a breach
        remain subject to applicable law and valid agreements.
      </Typography>

      <Typography variant="h5" gutterBottom>
        3. Cancellation After Travel to Pickup Begins
      </Typography>

      <Typography paragraph>
        Where previously agreed, a cancellation charge may account
        for documented travel to pickup, waiting time, or other
        specifically disclosed costs.
      </Typography>

      <Typography paragraph>
        A provider's internal decision to prepare for or travel
        toward an unconfirmed shipment does not by itself impose
        a charge on the Customer.
      </Typography>

      <Typography paragraph>
        An app status such as "dispatched" or "en route" is
        evidence to consider, not conclusive proof of an agreed
        charge or its amount. It does not mean that GoCourier Inc.
        has assumed the role of the performing carrier or
        appointed dispatcher.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Cancellation After Pickup
      </Typography>

      <Typography paragraph>
        After a Transportation Provider takes custody of cargo,
        stopping, returning, or redirecting the shipment requires
        communication with the authorized Customer and compliance
        with applicable custody and transportation obligations.
      </Typography>

      <Typography paragraph>
        The parties must address safe custody, a lawful destination,
        and any additional services. Return transportation,
        storage, redelivery, waiting, and handling charges require
        an applicable contractual or legal basis.
      </Typography>

      <Typography paragraph>
        Cancellation does not authorize abandoning cargo,
        withholding it unlawfully, or making unsafe movements.
        HaggleFreight support may assist with communication but
        does not replace either party's authority or legal duties.
      </Typography>

      <Typography variant="h5" gutterBottom>
        5. Customer-Caused Cancellation or Failed Access
      </Typography>

      <Typography paragraph>
        Customers must provide accurate shipment information,
        disclose material requirements, make cargo available as
        agreed, and provide relevant access instructions.
      </Typography>

      <Typography paragraph>
        Materially incorrect cargo details, unavailable access,
        unsafe conditions, or a no-show may justify refusal or
        cancellation. Financial consequences depend on the facts,
        accepted terms, and applicable law rather than an automatic
        penalty for every unsuccessful pickup.
      </Typography>

      <Typography paragraph>
        Both parties should communicate promptly and take
        reasonable steps to avoid unnecessary additional loss.
        Neither party may require unsafe or unlawful performance.
      </Typography>

      <Typography variant="h5" gutterBottom>
        6. Carrier Cancellation
      </Typography>

      <Typography paragraph>
        Transportation Providers may refuse or stop work that would
        be unsafe or unlawful, subject to their lawful duties
        concerning cargo already in their custody.
      </Typography>

      <Typography paragraph>
        For other cancellations, the provider must notify the
        Customer promptly and comply with its accepted agreement.
        Repeated unjustified failures, deception, or deliberate
        misuse may result in an account review.
      </Typography>

      <Typography paragraph>
        Declining an unaccepted opportunity or making a good-faith
        safety report is not itself grounds for a monetary penalty.
        An account restriction does not automatically determine
        liability for a particular shipment.
      </Typography>

      <Typography variant="h5" gutterBottom>
        7. HaggleFreight Restrictions or Removed Listings
      </Typography>

      <Typography paragraph>
        GoCourier Inc. may remove a listing, restrict access, or
        suspend use when reasonably necessary to address fraud,
        a safety risk, material policy violations, or legal
        requirements.
      </Typography>

      <Typography paragraph>
        Such action does not automatically extinguish an existing
        transportation agreement between users or authorize
        GoCourier Inc. to act as either party's transportation
        agent. We will communicate relevant restrictions where
        appropriate and lawful.
      </Typography>

      <Typography paragraph>
        Financial consequences remain subject to applicable
        accepted terms and law. Removing a listing does not by
        itself authorize keeping money otherwise owed to a user.
      </Typography>

      <Typography variant="h5" gutterBottom>
        8. Prohibited or Restricted Cargo
      </Typography>

      <Typography paragraph>
        A prohibited, illegal, or materially misdescribed shipment
        may be refused or removed. The Acceptable Use Policy
        identifies unsupported categories and relevant restrictions.
      </Typography>

      <Typography paragraph>
        A policy violation does not automatically cause forfeiture
        of every payment. Lawful payment restrictions, amounts
        actually due, required refunds, and legally required
        reporting must be assessed separately.
      </Typography>

      <Typography paragraph>
        Disclosure alone or another user's willingness to carry
        an item does not override a legal prohibition or an
        unsupported shipment category.
      </Typography>

      <Typography variant="h5" gutterBottom>
        9. Subscriptions and Recurring Services
      </Typography>

      <Typography paragraph>
        Cancelling a shipment does not automatically cancel a
        subscription. Cancelling a subscription does not
        automatically cancel an accepted shipment.
      </Typography>

      <Typography paragraph>
        When subscriptions are offered, cancellation of future
        renewal must be available through the disclosed purchase
        channel. No price, start date, fixed free period, or
        non-refundable subscription rule is created by this policy.
      </Typography>

      <Typography paragraph>
        Account deletion and subscription handling are addressed
        in the Terms of Service and Privacy Policy.
      </Typography>

      <Typography variant="h5" gutterBottom>
        10. Refunds Related to Cancellations
      </Typography>

      <Typography paragraph>
        The Refund Policy applies to payments affected by
        cancellation. The result depends on the payment made,
        services actually performed, accepted cancellation
        conditions, and applicable rights.
      </Typography>

      <Typography paragraph>
        An authorization hold, a captured payment, and an amount
        already paid to another party may require different
        processing steps. These distinctions do not remove an
        obligation to return money when a refund is legally due.
      </Typography>

      <Typography variant="h5" gutterBottom>
        11. Disputes
      </Typography>

      <Typography paragraph>
        We may review relevant agreements, messages, transaction
        records, timestamps, and other lawfully available evidence
        for support and actions within our responsibility.
      </Typography>

      <Typography paragraph>
        Support review is not binding arbitration and does not
        remove rights against another participant or through a
        payment provider, insurer, regulator, or court.
        A good-faith cancellation dispute is not itself misconduct.
      </Typography>

      <Typography paragraph>
        No support process shortens a nonwaivable claim deadline,
        authorizes an undisclosed charge, or permits confiscation
        of unrelated funds.
      </Typography>

      <Typography variant="h5" gutterBottom>
        12. Contact
      </Typography>

      <Typography paragraph>
        Contact support@hagglefreight.com with your account email,
        shipment identifier, relevant dates, and explanation.
        Notify the other transaction participant promptly rather
        than relying solely on a support request.
      </Typography>

      <Typography paragraph>
        Changes apply prospectively under the Terms of Service.
        Mandatory law and nonwaivable rights take priority.
      </Typography>
    </LegalPageLayout>
  );
};

export default CancellationPolicy;